import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Purchase, PurchaseHeader } from './models/purchase.model';
import { environment } from 'environments/environment';
import { SessionContextService } from 'app/core/helpers/session-context.helper';

/** Response from GET /purchase/paginated */
export interface PaginatedPurchasesResponse {
  data: any[];
  total: number;
  page: number;
  totalPages: number;
  metrics?: {
    activeCount: number;
    cancelledCount: number;
    filteredAmountSum: number;
    activeAmountSum: number;
  };
}

/** Query for GET /purchase/report — single object avoids long positional-arg calls (TS arity limits). */
export interface PurchaseReportQuery {
  fromDate?: string;
  toDate?: string;
  searchTerm?: string;
  supplierName?: string;
  itemName?: string;
  billType?: string;
  transactionNo?: string;
  invoiceNo?: string;
  storeName?: string;
  gstNumber?: string;
  minAmount?: number;
  maxAmount?: number;
  page?: number;
  limit?: number;
  billStatus?: 'ALL' | 'ACTIVE' | 'CANCELLED';
}

@Injectable({ providedIn: 'root' })
export class PurchaseService {
  private baseUrl = `${environment.apiUrl}/purchase`;
  constructor(
    private http: HttpClient,
    private sessionContext: SessionContextService,
  ) {}

  /**
   * Create a new purchase entry
   */
  createPurchase(dto: Purchase): Observable<PurchaseHeader> {
    return this.http.post<PurchaseHeader>(`${this.baseUrl}`, dto);
  }

  /**
   * Update a new purchase entry
   */
  updatePurchase(id: string, dto: Purchase): Observable<Purchase> {
    return this.http.put<Purchase>(`${this.baseUrl}/${id}`, dto); // PUT instead of POST
  }

  /**
   * Get list of all purchases
   */
  getAllPurchases(): Observable<PurchaseHeader[]> {
    return this.http.get<PurchaseHeader[]>(`${this.baseUrl}`);
  }

  /**
   * Get paginated purchases (latest first). Uses current store from session (header-selected store).
   */
  getPaginatedPurchases(
    page: number,
    limit: number,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    billStatus?: 'ALL' | 'ACTIVE' | 'CANCELLED',
  ) {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    const storeId = this.sessionContext.getStoreId();
    if (storeId) {
      params = params.set('storeId', storeId);
    }
    if (sortBy) {
      params = params.set('sortBy', sortBy);
    }
    if (sortOrder) {
      params = params.set('sortOrder', sortOrder);
    }
    if (billStatus) {
      params = params.set('billStatus', billStatus);
    }
    return this.http.get<PaginatedPurchasesResponse>(`${this.baseUrl}/paginated`, { params });
  }

  /**
   * Get list of all purchases records according to queries.
   * @returns Observable with either array (non-paginated) or paginated response object
   */
  getPurchaseReport(
    query?: PurchaseReportQuery,
  ): Observable<PurchaseHeader[] | { data: PurchaseHeader[]; total: number; page: number; limit: number; totalPages: number }> {
    const q = query ?? {};
    let params = new HttpParams();

    if (q.fromDate) params = params.set('fromDate', q.fromDate);
    if (q.toDate) params = params.set('toDate', q.toDate);
    if (q.searchTerm) params = params.set('searchTerm', q.searchTerm);
    if (q.supplierName) params = params.set('supplierName', q.supplierName);
    if (q.itemName) params = params.set('itemName', q.itemName);
    if (q.billType) params = params.set('billType', q.billType);
    if (q.transactionNo) params = params.set('transactionNo', q.transactionNo);
    if (q.invoiceNo) params = params.set('invoiceNo', q.invoiceNo);
    if (q.storeName) params = params.set('storeName', q.storeName);
    if (q.gstNumber) params = params.set('gstNumber', q.gstNumber);
    if (q.minAmount !== undefined && q.minAmount !== null) params = params.set('minAmount', q.minAmount.toString());
    if (q.maxAmount !== undefined && q.maxAmount !== null) params = params.set('maxAmount', q.maxAmount.toString());
    if (q.page) params = params.set('page', q.page.toString());
    if (q.limit) params = params.set('limit', q.limit.toString());
    if (q.billStatus) params = params.set('billStatus', q.billStatus);

    const storeId = this.sessionContext.getStoreId();
    if (storeId) {
      params = params.set('storeId', storeId);
    }

    return this.http.get<PurchaseHeader[] | { data: PurchaseHeader[]; total: number; page: number; limit: number; totalPages: number }>(`${this.baseUrl}/report`, { params });
  }

  /**
   * Get autocomplete suggestions for suppliers
   */
  getSupplierAutocomplete(search?: string, limit: number = 20): Observable<string[]> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (limit) params = params.set('limit', limit.toString());
    return this.http.get<string[]>(`${this.baseUrl}/autocomplete-suppliers`, { params });
  }

  /**
   * Get autocomplete suggestions for stores
   */
  getStoreAutocomplete(search?: string, limit: number = 20): Observable<string[]> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (limit) params = params.set('limit', limit.toString());
    return this.http.get<string[]>(`${this.baseUrl}/autocomplete-stores`, { params });
  }

  /**
   * Get autocomplete suggestions for items
   */
  getItemAutocomplete(search?: string, limit: number = 20): Observable<string[]> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (limit) params = params.set('limit', limit.toString());
    return this.http.get<string[]>(`${this.baseUrl}/autocomplete-items`, { params });
  }

  /**
   * Get autocomplete suggestions for GST numbers
   */
  getGstNumberAutocomplete(search?: string, limit: number = 20): Observable<string[]> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (limit) params = params.set('limit', limit.toString());
    return this.http.get<string[]>(`${this.baseUrl}/autocomplete-gst-numbers`, { params });
  }

  /**
   * Get autocomplete suggestions for invoice numbers
   */
  getInvoiceNoAutocomplete(search?: string, limit: number = 20): Observable<string[]> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (limit) params = params.set('limit', limit.toString());
    return this.http.get<string[]>(`${this.baseUrl}/autocomplete-invoice-numbers`, { params });
  }

  /**
   * Get a single purchase by ID
   */
  getPurchaseById(id: string): Observable<Purchase> {
    return this.http.get<Purchase>(`${this.baseUrl}/${id}`);
  }

  /** Loads purchase + supplier's prior purchase invoice no for hardcoded print (Old Bill). */
  getPurchaseByIdForHardcodedPrint(id: string): Observable<any> {
    return this.getPurchaseById(id).pipe(
      switchMap((purchase) => this.attachPreviousPurchaseInvoiceForPrint(purchase)),
    );
  }

  attachPreviousPurchaseInvoiceForPrint(document: any): Observable<any> {
    const supplierId = document?.supplierId || document?.supplier?.id;
    if (!supplierId) {
      return of(document);
    }
    return this.getSupplierPurchases(supplierId, 1, 10).pipe(
      map((res: { data?: any[] }) => {
        const list = res?.data ?? [];
        const previous =
          list.find((entry: any) => entry.id !== document.id) ?? list[0];
        const invoiceNo = String(previous?.invoiceNo ?? '').trim();
        return invoiceNo ? { ...document, previousPurchaseInvoiceNo: invoiceNo } : document;
      }),
      catchError(() => of(document)),
    );
  }

  /**
   * Delete a purchase by ID
   */
  deletePurchase(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  /**
   * Cancel purchase bill: reverses stock and outstanding, then removes the record.
   */
  cancelPurchase(id: string, deletedBy?: string): Observable<{ message: string }> {
    return this.http.request<{ message: string }>('DELETE', `${this.baseUrl}/${id}/cancel`, {
      body: deletedBy ? { deletedBy } : {},
    });
  }

  getLastPurchaseDetails(itemId: string, supplierId: string): Observable<any> {
    const params = new HttpParams().set('itemId', itemId).set('supplierId', supplierId);
    return this.http.get<any>(`${this.baseUrl}/last-purchase-details`, {
      params,
    });
  }


  getPurchaseByTransactionNo(trxNo: number, trxDate?: string) {
    return this.http.get(`${this.baseUrl}/by-transaction/${trxNo}`, {
      params: trxDate ? { transactionDate: trxDate } : {},
    });
  }

  getSupplierPurchases(supplierId: string, page = 1, limit = 10, financialYear?: string, search?: string) {
    const params: any = { page, limit };
    if (financialYear) params.financialYear = financialYear;
    if (search && search.trim()) params.search = search.trim();

    return this.http.get(`${this.baseUrl}/by-supplier/${supplierId}`, {
      params,
    });
  }

  /**
   * @deprecated Use getNextTransactionNo() instead - this uses the old transaction counter system
   */
  getPreviewTransaction() {
    return this.http.get<{ transactionNo: number; financialYear: string }>(
      `${this.baseUrl}/preview-transaction`,
    );
  }

  validateInvoiceNumber(
    supplierId: string,
    invoiceNo: string,
    invoiceDate?: Date | string | null,
  ) {
    const params: Record<string, string> = {
      supplierId,
      invoiceNo,
    };
    if (invoiceDate != null && invoiceDate !== '') {
      const dateStr =
        typeof invoiceDate === 'string'
          ? invoiceDate
          : invoiceDate instanceof Date
            ? invoiceDate.toISOString()
            : '';
      if (dateStr) params['invoiceDate'] = dateStr;
    }
    return this.http.get<any>(`${this.baseUrl}/validate/invoice`, {
      params,
    });
  }
  /**
   * Get next available transaction number
   */
  getNextTransactionNo(): Observable<{ transactionNo: number }> {
    return this.http.get<{ transactionNo: number }>(`${this.baseUrl}/preview-transaction-no`);
  }

  /**
   * Export purchase report
   */
  exportPurchaseReport(params: any): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/export`, params, { responseType: 'blob' });
  }

  /**
   * Update roundoff and final amount for a purchase
   */
  updateRoundoff(purchaseId: string, roundoffData: {
    roundOff: number;
    finalAmount: number;
    remarks?: string;
  }): Observable<any> {
    return this.http.put(`${this.baseUrl}/${purchaseId}/roundoff`, roundoffData);
  }

  /**
   * Update final rounding amount for a purchase
   * This will recalculate amountOutstanding = finalRoundingAmount - amountPaid
   */
  updateFinalRounding(purchaseId: string, finalRoundingAmount: number, amountPaid?: number): Observable<any> {
    const payload: any = { finalRoundingAmount };
    
    if (amountPaid !== undefined) {
      payload.amountPaid = amountPaid;
    }

    return this.http.put<any>(
      `${this.baseUrl}/${purchaseId}/final-rounding`,
      payload
    );
  }

  /**
   * Increment print count for a purchase
   */
  incrementPrintCount(purchaseId: string): Observable<{ success: boolean; printCount: number }> {
    return this.http.post<{ success: boolean; printCount: number }>(
      `${this.baseUrl}/${purchaseId}/print-count`,
      {}
    );
  }

  /**
   * Get lowest purchase rate for an item with matching MRP
   * Used for rate comparison indicator (best deal comparison)
   */
  getLowestRateForMrp(itemId: string, mrp: number, storeId?: string): Observable<LowestRateForMrpResponse | null> {
    let params = new HttpParams().set('mrp', mrp.toString());
    if (storeId) {
      params = params.set('storeId', storeId);
    }
    return this.http.get<LowestRateForMrpResponse | null>(
      `${this.baseUrl}/item-lowest-rate/${itemId}`,
      { params }
    );
  }

  /**
   * Get lowest purchase rate for multiple (itemId, mrp) pairs in one call (bulk).
   * Used during purchase import to avoid N+1 item-lowest-rate API calls.
   * Returns a map keyed by `${itemId}:${mrp}` -> data (or null).
   */
  getLowestRateForMrpBulk(
    items: { itemId: string; mrp: number }[],
    storeId?: string,
  ): Observable<Record<string, LowestRateForMrpResponse | null>> {
    if (!items?.length) return of({});
    return this.http.post<Record<string, LowestRateForMrpResponse | null>>(
      `${this.baseUrl}/item-lowest-rate-bulk`,
      { items, storeId }
    );
  }

  /**
   * Get last purchase rates for an item (single most recent purchase line).
   * Used to proportionally prefill sale rate, MRP, wholesale, special rates when user enters a new purchase rate.
   */
  getLastPurchaseRates(itemId: string): Observable<LastPurchaseRatesResponse> {
    return this.http.get<LastPurchaseRatesResponse>(`${this.baseUrl}/item/${itemId}/last-purchase-rates`);
  }

  /**
   * Get last purchase rates for multiple items in one call (bulk).
   * Used during purchase import to avoid N+1 API calls. Returns a map of itemId -> data (or null).
   */
  getLastPurchaseRatesBulk(itemIds: string[]): Observable<Record<string, LastPurchaseRatesResponse | null>> {
    if (!itemIds?.length) {
      return of({});
    }
    return this.http.post<Record<string, LastPurchaseRatesResponse | null>>(
      `${this.baseUrl}/last-purchase-rates-bulk`,
      { itemIds }
    );
  }

  /**
   * Get attachment count for a purchase
   */
  getPurchaseAttachmentCount(purchaseId: string): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.baseUrl}/${purchaseId}/attachment-count`);
  }

  /**
   * Link temp attachments to a purchase after it's saved
   */
  linkTempAttachments(purchaseId: string, tempAttachments: any[]): Observable<any> {
    return this.http.post(`${this.baseUrl}/${purchaseId}/link-temp-attachments`, {
      attachments: tempAttachments
    });
  }

  /**
   * Link bill file(s) to a purchase (called when purchase form is saved with pending bills)
   */
  linkBill(purchaseId: string, files: File[]): Observable<any> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return this.http.post(`${this.baseUrl}/${purchaseId}/link-bill`, formData);
  }
}

export interface LastPurchaseRatesResponse {
  itemId: string;
  purchaseRate: number | null;
  saleRate: number | null;
  mrp: number | null;
  wholesaleRate: number | null;
  specialRate: number | null;
  specialRate2: number | null;
}

export interface LowestRateForMrpResponse {
  rate: number | null;
  mrp: number | null;
  purchaseDate: Date;
  actualCostRate: number | null;
}